import {addDays} from 'date-fns';
import {Button, ButtonGroup, Choice, DatePicker, Pagination, Search} from 'platform/components';
import {Box, Hide, HStack, Stack, Text, useDevice, VStack} from 'platform/foundation';

import {BranchResponseBody} from '@dms/api/shared';
import i18n from '@dms/i18n';
import {NoPermissionTooltip} from '@dms/shared';

import {getApiDateString, parseDate, RequiredTestIdProps, suffixTestId} from 'shared/web';

import {TEST_DRIVE_CALENDAR} from '../constants/TEST_DRIVE_CALENDAR';
import {openTestDriveCalendarSettingsDialog} from '../utils/openTestDriveCalendarSettingsDialog';

const shiftSelectedDate = (selectedDate: string, dayOffset: number) =>
  getApiDateString(addDays(parseDate(selectedDate), dayOffset));

interface TestDriveCalendarToolbarProps extends RequiredTestIdProps {
  rangeMode: number;
  selectedDate: string;
  assignedBranches: BranchResponseBody[];
  search: string | null;
  page: number;
  size: number;
  totalCount: number;
  hasCreateTestDrivePermission: boolean;
  onCreateTestDrive: () => void;
  onRangeModeChange: (value: number) => void;
  onSelectedDateChange: (value: string) => void;
  onSearchChange: (value: string | null) => void;
  onPageChange: (value: number) => void;
  onSizeChange: (value: number) => void;
}

export function TestDriveCalendarToolbar(props: TestDriveCalendarToolbarProps) {
  const openSettingsDialog = () =>
    openTestDriveCalendarSettingsDialog({
      assignedBranches: props.assignedBranches,
      'data-testid': suffixTestId('settingsDialog', props),
    });

  const isMobile = useDevice() === 'mobile';
  const totalPages = Math.max(Math.ceil(props.totalCount / props.size), 1);

  return (
    <VStack spacing={2}>
      <HStack spacing={2} align="center" wrap>
        <HStack spacing={2} align="center" justify="space-between" width={['100%', undefined]} wrap>
          <ButtonGroup>
            <Button
              data-testid={suffixTestId('range-week', props)}
              title={i18n.t('page.testDrive.labels.sevenDays')}
              variant={
                props.rangeMode === TEST_DRIVE_CALENDAR.RANGE_DAYS.WEEK ? 'primary' : 'secondary'
              }
              onClick={() => props.onRangeModeChange(TEST_DRIVE_CALENDAR.RANGE_DAYS.WEEK)}
            />
            <Button
              data-testid={suffixTestId('range-month', props)}
              title={i18n.t('page.testDrive.labels.thirtyDays')}
              variant={
                props.rangeMode === TEST_DRIVE_CALENDAR.RANGE_DAYS.MONTH ? 'primary' : 'secondary'
              }
              onClick={() => props.onRangeModeChange(TEST_DRIVE_CALENDAR.RANGE_DAYS.MONTH)}
            />
          </ButtonGroup>

          <HStack spacing={2}>
            <Button
              data-testid={suffixTestId('previousPeriod', props)}
              leftIcon="navigation/chevron_left"
              variant="secondary"
              onClick={() =>
                props.onSelectedDateChange(shiftSelectedDate(props.selectedDate, -props.rangeMode))
              }
            />
            <Button
              data-testid={suffixTestId('today', props)}
              title={i18n.t('general.labels.today')}
              variant="secondary"
              onClick={() => props.onSelectedDateChange(getApiDateString(new Date()))}
            />
            <Button
              data-testid={suffixTestId('nextPeriod', props)}
              leftIcon="navigation/chevron_right"
              variant="secondary"
              onClick={() =>
                props.onSelectedDateChange(shiftSelectedDate(props.selectedDate, props.rangeMode))
              }
            />
          </HStack>
        </HStack>

        <HStack spacing={2} align="center" justify="space-between" grow={1}>
          <Box flexGrow={[1, 0]} width={[undefined, 40]}>
            <DatePicker
              data-testid={suffixTestId('selectedDate', props)}
              value={parseDate(props.selectedDate)}
              onChange={(value) =>
                props.onSelectedDateChange(getApiDateString(value ?? new Date()))
              }
            />
          </Box>

          <HStack spacing={2} align="center">
            <Button
              aria-label={i18n.t('general.labels.settings')}
              leftIcon="action/settings"
              variant="secondary"
              onClick={openSettingsDialog}
              data-testid={suffixTestId('settings', props)}
            />

            <NoPermissionTooltip shouldShowTooltip={!props.hasCreateTestDrivePermission}>
              <Button
                aria-label={i18n.t('entity.testDrive.labels.createTestDrive')}
                title={isMobile ? undefined : i18n.t('entity.testDrive.labels.createTestDrive')}
                leftIcon="content/add_circle"
                variant="primary"
                isDisabled={!props.hasCreateTestDrivePermission}
                onClick={props.onCreateTestDrive}
                data-testid={suffixTestId('createTestDrive', props)}
              />
            </NoPermissionTooltip>
          </HStack>
        </HStack>
      </HStack>

      <Stack
        direction={['column', 'row']}
        spacing={2}
        align={['stretch', 'center']}
        justify="space-between"
      >
        <Box width={['100%', 70]}>
          <Search
            data-testid={suffixTestId('search', props)}
            value={props.search}
            onChange={props.onSearchChange}
          />
        </Box>

        <HStack spacing={4} align="center" justify="flex-end" wrap>
          <Text size="xSmall" color="secondary">
            {i18n.t('page.testDrive.labels.totalVehicles', {count: props.totalCount})}
          </Text>
          <Hide when={props.totalCount === 0}>
            <Pagination
              data-testid={suffixTestId('pagination', props)}
              page={props.page}
              pagesQuantity={totalPages}
              onPageChange={props.onPageChange}
            />
          </Hide>
          <Box width={25}>
            <Choice
              data-testid={suffixTestId('size', props)}
              value={props.size}
              isNotClearable
              options={TEST_DRIVE_CALENDAR.PAGE_SIZES.map((pageSize) => ({
                value: pageSize,
                label: String(pageSize),
              }))}
              onChange={(value) =>
                props.onSizeChange(value ?? TEST_DRIVE_CALENDAR.DEFAULT_PAGE_SIZE)
              }
            />
          </Box>
        </HStack>
      </Stack>
    </VStack>
  );
}
